import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixStoresCatalogV3CatalogApiUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/stores',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/stores/v3/products',
        destPath: '/v3/products',
      },
      {
        srcPath: '/stores/v3/bulk/products',
        destPath: '/v3/bulk/products',
      },
      {
        srcPath: '/stores/v3/products-with-inventory',
        destPath: '/v3/products-with-inventory',
      },
      {
        srcPath: '/stores/v3/bulk/products-with-inventory',
        destPath: '/v3/bulk/products-with-inventory',
      },
      {
        srcPath: '/stores/v3/all-products-category',
        destPath: '/v3/all-products-category',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/stores-catalog-orchestrator',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_stores_products-v-3';

/**
 * Creates a new product.
 *
 * You can also add a ribbon, brand, info sections, options, and modifiers in the same request.
 *
 * > **Notes:**
 * > + This method doesn't create inventory items. Use [Create Product With Inventory](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/create-product-with-inventory) to create a product with inventory in a single call, or create inventory items separately using the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction).
 * > + When you create a product with inline options or modifiers (without referencing existing customization IDs), new standalone [Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/introduction) entities are automatically created. These can be reused across other products.
 */
export function createProduct(payload: object): RequestOptionsFactory<any> {
  function __createProduct({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'product.createdDate' },
          { path: 'product.updatedDate' },
          { path: 'product.description.metadata.createdTimestamp' },
          { path: 'product.description.metadata.updatedTimestamp' },
          { path: 'product.media.main.image.urlExpirationDate' },
          { path: 'product.media.main.video.urlExpirationDate' },
          { path: 'product.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'product.media.main.video.posters.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'product.infoSections.description.metadata.updatedTimestamp',
          },
          {
            path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'product.physicalProperties.pricePerUnit.quantity' },
          { path: 'product.physicalProperties.shippingWeightRange.minValue' },
          { path: 'product.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          { path: 'product.variantsInfo.variants.revenueDetails.profitMargin' },
          {
            path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
          },
          { path: 'product.minVariantPriceInfo.revenueDetails.profitMargin' },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'product.description.nodes.galleryData.options.item.ratio' },
          { path: 'product.description.nodes.imageData.image.duration' },
          { path: 'product.description.nodes.mapData.mapSettings.lat' },
          { path: 'product.description.nodes.mapData.mapSettings.lng' },
          { path: 'product.description.nodes.pollData.poll.image.duration' },
          {
            path: 'product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'product.description.nodes.appEmbedData.image.duration' },
          { path: 'product.description.nodes.videoData.video.duration' },
          { path: 'product.description.nodes.videoData.thumbnail.duration' },
          { path: 'product.description.nodes.audioData.audio.duration' },
          { path: 'product.description.nodes.audioData.coverImage.duration' },
          {
            path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'product.description.nodes.shapeData.shape.duration' },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.description.nodes.tocData.fontSize' },
          { path: 'product.description.nodes.tocData.itemSpacing' },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'product.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'product.variantsInfo.variants.physicalProperties.weight' },
          { path: 'product.minVariantPriceInfo.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.CreateProduct',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createProduct;
}

/**
 * Creates a new product with its inventory in a single call.
 *
 * Pass `variantsInfo.variants.inventoryItem` to create inventory items for each variant in the default location. Each inventory item can track stock using either `quantity` (for exact stock counts) or `inStock` (for simple availability). If `inventoryItem` isn't provided, no inventory items are created.
 *
 * You can also add a ribbon, brand, info sections, options, and modifiers in the same request.
 *
 * Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management) including tracking methods, preorder settings, and multi-location inventory.
 *
 * > **Note:** When you create a product with inline options or modifiers (without referencing existing customization IDs), new standalone [Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/introduction) entities are automatically created. These can be reused across other products.
 */
export function createProductWithInventory(
  payload: object
): RequestOptionsFactory<any> {
  function __createProductWithInventory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'product.physicalProperties.pricePerUnit.quantity' },
          { path: 'product.physicalProperties.shippingWeightRange.minValue' },
          { path: 'product.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          { path: 'product.variantsInfo.variants.revenueDetails.profitMargin' },
          {
            path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'product.description.nodes.galleryData.options.item.ratio' },
          { path: 'product.description.nodes.imageData.image.duration' },
          { path: 'product.description.nodes.mapData.mapSettings.lat' },
          { path: 'product.description.nodes.mapData.mapSettings.lng' },
          { path: 'product.description.nodes.pollData.poll.image.duration' },
          {
            path: 'product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'product.description.nodes.appEmbedData.image.duration' },
          { path: 'product.description.nodes.videoData.video.duration' },
          { path: 'product.description.nodes.videoData.thumbnail.duration' },
          { path: 'product.description.nodes.audioData.audio.duration' },
          { path: 'product.description.nodes.audioData.coverImage.duration' },
          {
            path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'product.description.nodes.shapeData.shape.duration' },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.description.nodes.tocData.fontSize' },
          { path: 'product.description.nodes.tocData.itemSpacing' },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'product.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'product.variantsInfo.variants.physicalProperties.weight' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'product.description.metadata.createdTimestamp' },
          { path: 'product.description.metadata.updatedTimestamp' },
          { path: 'product.media.main.image.urlExpirationDate' },
          { path: 'product.media.main.video.urlExpirationDate' },
          { path: 'product.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'product.media.main.video.posters.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'product.infoSections.description.metadata.updatedTimestamp',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.CreateProductWithInventory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products-with-inventory',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
              { path: 'inventoryResults.results.item.createdDate' },
              { path: 'inventoryResults.results.item.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createProductWithInventory;
}

/**
 * Updates a product.
 *
 * Each time the product is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the product.
 * This ensures you're working with the latest product and prevents unintended overwrites.
 *
 * >**Notes:**
 * > + To update array fields like `options`, `modifiers`, `variantsInfo.variants`, and any others, you must pass the entire existing array. Passing only the changed item overwrites the entire array, not just merge the single change.
 * > + To update `variantsInfo.variants` you must also pass `options` and vice versa. This is because variants and options are mutually dependent and must be aligned.
 * > + To update existing `variantsInfo.variants`, make sure to provide `variantsInfo.variants.id`. If no ID is passed, a variant is created with a new ID.
 * > + Updating media per variant isn't supported. Instead, assign media items to product option choices using the `linkedMedia` field. You must use preexisting product media.
 */
export function updateProduct(payload: object): RequestOptionsFactory<any> {
  function __updateProduct({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'product.createdDate' },
          { path: 'product.updatedDate' },
          { path: 'product.description.metadata.createdTimestamp' },
          { path: 'product.description.metadata.updatedTimestamp' },
          { path: 'product.media.main.image.urlExpirationDate' },
          { path: 'product.media.main.video.urlExpirationDate' },
          { path: 'product.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'product.media.main.video.posters.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'product.infoSections.description.metadata.updatedTimestamp',
          },
          {
            path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'product.physicalProperties.pricePerUnit.quantity' },
          { path: 'product.physicalProperties.shippingWeightRange.minValue' },
          { path: 'product.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          { path: 'product.variantsInfo.variants.revenueDetails.profitMargin' },
          {
            path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
          },
          { path: 'product.minVariantPriceInfo.revenueDetails.profitMargin' },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'product.description.nodes.galleryData.options.item.ratio' },
          { path: 'product.description.nodes.imageData.image.duration' },
          { path: 'product.description.nodes.mapData.mapSettings.lat' },
          { path: 'product.description.nodes.mapData.mapSettings.lng' },
          { path: 'product.description.nodes.pollData.poll.image.duration' },
          {
            path: 'product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'product.description.nodes.appEmbedData.image.duration' },
          { path: 'product.description.nodes.videoData.video.duration' },
          { path: 'product.description.nodes.videoData.thumbnail.duration' },
          { path: 'product.description.nodes.audioData.audio.duration' },
          { path: 'product.description.nodes.audioData.coverImage.duration' },
          {
            path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'product.description.nodes.shapeData.shape.duration' },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.description.nodes.tocData.fontSize' },
          { path: 'product.description.nodes.tocData.itemSpacing' },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'product.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'product.variantsInfo.variants.physicalProperties.weight' },
          { path: 'product.minVariantPriceInfo.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'PATCH' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.UpdateProduct',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/{product.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateProduct;
}

/**
 * Updates a product and its inventory in a single call.
 *
 * Each time the product is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the product.
 * This ensures you're working with the latest product and prevents unintended overwrites.
 *
 * Each inventory item can track stock using either `quantity` (for exact stock counts) or `inStock` (for simple availability). Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management) including tracking methods, preorder settings, and multi-location inventory.
 *
 * >**Notes:**
 * > + To update array fields like `options`, `modifiers`, `variantsInfo.variants`, and any others, you must pass the entire existing array. Passing only the changed item overwrites the entire array, not just merge the single change.
 * > + To update `variantsInfo.variants` you must also pass `options` and vice versa. This is because variants and options are mutually dependent and must be aligned.
 * > + To update existing `variantsInfo.variants`, make sure to provide `variantsInfo.variants.id`. If no ID is passed, a variant is created with a new ID.
 * > + Updating media per variant isn't supported. Instead, assign media items to product option choices using the `linkedMedia` field. You must use preexisting product media.
 */
export function updateProductWithInventory(
  payload: object
): RequestOptionsFactory<any> {
  function __updateProductWithInventory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'product.physicalProperties.pricePerUnit.quantity' },
          { path: 'product.physicalProperties.shippingWeightRange.minValue' },
          { path: 'product.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          { path: 'product.variantsInfo.variants.revenueDetails.profitMargin' },
          {
            path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'product.description.nodes.galleryData.options.item.ratio' },
          { path: 'product.description.nodes.imageData.image.duration' },
          { path: 'product.description.nodes.mapData.mapSettings.lat' },
          { path: 'product.description.nodes.mapData.mapSettings.lng' },
          { path: 'product.description.nodes.pollData.poll.image.duration' },
          {
            path: 'product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'product.description.nodes.appEmbedData.image.duration' },
          { path: 'product.description.nodes.videoData.video.duration' },
          { path: 'product.description.nodes.videoData.thumbnail.duration' },
          { path: 'product.description.nodes.audioData.audio.duration' },
          { path: 'product.description.nodes.audioData.coverImage.duration' },
          {
            path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'product.description.nodes.shapeData.shape.duration' },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.description.nodes.tocData.fontSize' },
          { path: 'product.description.nodes.tocData.itemSpacing' },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'product.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'product.variantsInfo.variants.physicalProperties.weight' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'product.description.metadata.createdTimestamp' },
          { path: 'product.description.metadata.updatedTimestamp' },
          { path: 'product.media.main.image.urlExpirationDate' },
          { path: 'product.media.main.video.urlExpirationDate' },
          { path: 'product.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'product.media.main.video.posters.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'product.infoSections.description.metadata.updatedTimestamp',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'PATCH' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.UpdateProductWithInventory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products-with-inventory/{product.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
              { path: 'inventoryResults.results.item.createdDate' },
              { path: 'inventoryResults.results.item.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateProductWithInventory;
}

/**
 * Creates up to 100 products.
 *
 * >**Note:**
 * > The following limits apply to the total number of creatable entities in a single request.
 * >
 * > + `options`: 100
 * > + `modifiers`: 100
 * > + `infoSections`: 100
 * > + `variantsInfo.variants`: 1000
 * >
 * > For example, you can create 10 products with up to 10 options for each product (10 x 10 = 100), or 1 product with 100 options.
 * > Alternatively, you can create 100 products with up to 10 variants in each (100 x 10 = 1000), or 1 product with 1000 variants.
 */
export function bulkCreateProducts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateProducts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.createdDate' },
          { path: 'products.updatedDate' },
          { path: 'products.description.metadata.createdTimestamp' },
          { path: 'products.description.metadata.updatedTimestamp' },
          { path: 'products.media.main.image.urlExpirationDate' },
          { path: 'products.media.main.video.urlExpirationDate' },
          { path: 'products.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'products.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'products.media.main.video.posters.urlExpirationDate' },
          { path: 'products.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'products.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'products.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'products.infoSections.description.metadata.updatedTimestamp',
          },
          {
            path: 'products.variantsInfo.variants.media.image.urlExpirationDate',
          },
          {
            path: 'products.variantsInfo.variants.media.video.urlExpirationDate',
          },
          {
            path: 'products.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.variantsInfo.variants.media.video.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'products.physicalProperties.pricePerUnit.quantity' },
          { path: 'products.physicalProperties.shippingWeightRange.minValue' },
          { path: 'products.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'products.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          {
            path: 'products.variantsInfo.variants.revenueDetails.profitMargin',
          },
          {
            path: 'products.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'products.minVariantPriceInfo.pricePerUnitData.settings.quantity',
          },
          { path: 'products.minVariantPriceInfo.revenueDetails.profitMargin' },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'products.description.nodes.galleryData.options.item.ratio' },
          { path: 'products.description.nodes.imageData.image.duration' },
          { path: 'products.description.nodes.mapData.mapSettings.lat' },
          { path: 'products.description.nodes.mapData.mapSettings.lng' },
          { path: 'products.description.nodes.pollData.poll.image.duration' },
          {
            path: 'products.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'products.description.nodes.appEmbedData.image.duration' },
          { path: 'products.description.nodes.videoData.video.duration' },
          { path: 'products.description.nodes.videoData.thumbnail.duration' },
          { path: 'products.description.nodes.audioData.audio.duration' },
          { path: 'products.description.nodes.audioData.coverImage.duration' },
          {
            path: 'products.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'products.description.nodes.shapeData.shape.duration' },
          {
            path: 'products.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.description.nodes.tocData.fontSize' },
          { path: 'products.description.nodes.tocData.itemSpacing' },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'products.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'products.variantsInfo.variants.physicalProperties.weight' },
          { path: 'products.minVariantPriceInfo.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkCreateProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.description.metadata.createdTimestamp' },
              { path: 'results.item.description.metadata.updatedTimestamp' },
              { path: 'results.item.media.main.image.urlExpirationDate' },
              { path: 'results.item.media.main.video.urlExpirationDate' },
              {
                path: 'results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.description.nodes.tocData.fontSize' },
              { path: 'results.item.description.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'results.item.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateProducts;
}

/**
 * Creates up to 100 products, and can create the products' inventories in the variants' default locations.
 *
 * >**Note:**
 * > The following limits apply to the total number of creatable entities in a single request.
 * >
 * > + `options`: 100
 * > + `modifiers`: 100
 * > + `infoSections`: 100
 * > + `variantsInfo.variants`: 1000
 * >
 * > For example, you can create 10 products with up to 10 options for each product (10 x 10 = 100), or 1 product with 100 options.
 * > Alternatively, you can create 100 products with up to 10 variants in each (100 x 10 = 1000), or 1 product with 1000 variants.
 */
export function bulkCreateProductsWithInventory(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateProductsWithInventory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'products.physicalProperties.pricePerUnit.quantity' },
          { path: 'products.physicalProperties.shippingWeightRange.minValue' },
          { path: 'products.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'products.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          {
            path: 'products.variantsInfo.variants.revenueDetails.profitMargin',
          },
          {
            path: 'products.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'products.description.nodes.galleryData.options.item.ratio' },
          { path: 'products.description.nodes.imageData.image.duration' },
          { path: 'products.description.nodes.mapData.mapSettings.lat' },
          { path: 'products.description.nodes.mapData.mapSettings.lng' },
          { path: 'products.description.nodes.pollData.poll.image.duration' },
          {
            path: 'products.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'products.description.nodes.appEmbedData.image.duration' },
          { path: 'products.description.nodes.videoData.video.duration' },
          { path: 'products.description.nodes.videoData.thumbnail.duration' },
          { path: 'products.description.nodes.audioData.audio.duration' },
          { path: 'products.description.nodes.audioData.coverImage.duration' },
          {
            path: 'products.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'products.description.nodes.shapeData.shape.duration' },
          {
            path: 'products.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.description.nodes.tocData.fontSize' },
          { path: 'products.description.nodes.tocData.itemSpacing' },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'products.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'products.variantsInfo.variants.physicalProperties.weight' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.description.metadata.createdTimestamp' },
          { path: 'products.description.metadata.updatedTimestamp' },
          { path: 'products.media.main.image.urlExpirationDate' },
          { path: 'products.media.main.video.urlExpirationDate' },
          { path: 'products.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'products.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'products.media.main.video.posters.urlExpirationDate' },
          { path: 'products.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'products.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'products.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'products.infoSections.description.metadata.updatedTimestamp',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkCreateProductsWithInventory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products-with-inventory/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'productResults.results.item.createdDate' },
              { path: 'productResults.results.item.updatedDate' },
              {
                path: 'productResults.results.item.description.metadata.createdTimestamp',
              },
              {
                path: 'productResults.results.item.description.metadata.updatedTimestamp',
              },
              {
                path: 'productResults.results.item.media.main.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'productResults.results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
              { path: 'inventoryResults.results.item.createdDate' },
              { path: 'inventoryResults.results.item.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'productResults.results.item.physicalProperties.pricePerUnit.quantity',
              },
              {
                path: 'productResults.results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'productResults.results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'productResults.results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'productResults.results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'productResults.results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.tocData.fontSize',
              },
              {
                path: 'productResults.results.item.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.physicalProperties.weight',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.weight',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateProductsWithInventory;
}

/**
 * Updates up to 100 products.
 *
 * >**Note:**
 * > The following limits apply to the total number of updatable entities in a single request.
 * > For example, you can update 10 products with up to 10 options for each product (10 x 10 = 100), or one product with 100 options.
 * > Alternatively, you can update 100 products with up to 10 variants in each (100 x 10 = 1000), or one product with 1000 variants.
 * > + `options`: 100
 * > + `modifiers`: 100
 * > + `infoSections`: 100
 * > + `variantsInfo.variants`: 1000
 */
export function bulkUpdateProducts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProducts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'products.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.product.createdDate' },
          { path: 'products.product.updatedDate' },
          { path: 'products.product.description.metadata.createdTimestamp' },
          { path: 'products.product.description.metadata.updatedTimestamp' },
          { path: 'products.product.media.main.image.urlExpirationDate' },
          { path: 'products.product.media.main.video.urlExpirationDate' },
          {
            path: 'products.product.media.main.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.image.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'products.product.infoSections.description.metadata.updatedTimestamp',
          },
          {
            path: 'products.product.variantsInfo.variants.media.image.urlExpirationDate',
          },
          {
            path: 'products.product.variantsInfo.variants.media.video.urlExpirationDate',
          },
          {
            path: 'products.product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.variantsInfo.variants.media.video.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'products.product.physicalProperties.pricePerUnit.quantity' },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.minValue',
          },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.maxValue',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          {
            path: 'products.product.variantsInfo.variants.revenueDetails.profitMargin',
          },
          {
            path: 'products.product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'products.product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
          },
          {
            path: 'products.product.minVariantPriceInfo.revenueDetails.profitMargin',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.product.description.nodes.tocData.fontSize' },
          { path: 'products.product.description.nodes.tocData.itemSpacing' },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.fontSize',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.weight',
          },
          { path: 'products.product.minVariantPriceInfo.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkUpdateProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.description.metadata.createdTimestamp' },
              { path: 'results.item.description.metadata.updatedTimestamp' },
              { path: 'results.item.media.main.image.urlExpirationDate' },
              { path: 'results.item.media.main.video.urlExpirationDate' },
              {
                path: 'results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.description.nodes.tocData.fontSize' },
              { path: 'results.item.description.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'results.item.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateProducts;
}

/**
 * Updates up to 100 products, and can update the products' inventories in the variants' default locations.
 *
 * >**Note:**
 * > The following limits apply to the total number of updatable entities in a single request.
 * > For example, you can update 10 products with up to 10 options for each product (10 x 10 = 100), or one product with 100 options.
 * > Alternatively, you can update 100 products with up to 10 variants in each (100 x 10 = 1000), or one product with 1000 variants.
 * > + `options`: 100
 * > + `modifiers`: 100
 * > + `infoSections`: 100
 * > + `variantsInfo.variants`: 1000
 */
export function bulkUpdateProductsWithInventory(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProductsWithInventory({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'products.fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'products.product.physicalProperties.pricePerUnit.quantity' },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.minValue',
          },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.maxValue',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          {
            path: 'products.product.variantsInfo.variants.revenueDetails.profitMargin',
          },
          {
            path: 'products.product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'products.product.description.nodes.tocData.fontSize' },
          { path: 'products.product.description.nodes.tocData.itemSpacing' },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.fontSize',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.weight',
          },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.product.description.metadata.createdTimestamp' },
          { path: 'products.product.description.metadata.updatedTimestamp' },
          { path: 'products.product.media.main.image.urlExpirationDate' },
          { path: 'products.product.media.main.video.urlExpirationDate' },
          {
            path: 'products.product.media.main.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.image.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'products.product.infoSections.description.metadata.updatedTimestamp',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkUpdateProductsWithInventory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products-with-inventory/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'productResults.results.item.createdDate' },
              { path: 'productResults.results.item.updatedDate' },
              {
                path: 'productResults.results.item.description.metadata.createdTimestamp',
              },
              {
                path: 'productResults.results.item.description.metadata.updatedTimestamp',
              },
              {
                path: 'productResults.results.item.media.main.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'productResults.results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
              { path: 'inventoryResults.results.item.createdDate' },
              { path: 'inventoryResults.results.item.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'productResults.results.item.physicalProperties.pricePerUnit.quantity',
              },
              {
                path: 'productResults.results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'productResults.results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'productResults.results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'productResults.results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'productResults.results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.description.nodes.tocData.fontSize',
              },
              {
                path: 'productResults.results.item.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'productResults.results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'productResults.results.item.variantsInfo.variants.physicalProperties.weight',
              },
              {
                path: 'productResults.results.item.minVariantPriceInfo.weight',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateProductsWithInventory;
}

/**
 * Updates multiple products, given the provided filter.
 *
 * To update `infoSections`, `brand` or `ribbon` fields, you must also pass their existing `id`.
 *
 * > **Note:**
 * > The following fields can't be updated with this endpoint:
 * > + `slug`
 * > + `options`
 * > + `modifiers`
 * > + `variantsInfo`
 * >
 * > To update these fields, use [Bulk Update Products](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/bulk-update-products).
 */
export function bulkUpdateProductsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProductsByFilter({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'product.createdDate' },
          { path: 'product.updatedDate' },
          { path: 'product.description.metadata.createdTimestamp' },
          { path: 'product.description.metadata.updatedTimestamp' },
          { path: 'product.media.main.image.urlExpirationDate' },
          { path: 'product.media.main.video.urlExpirationDate' },
          { path: 'product.media.main.video.resolutions.urlExpirationDate' },
          {
            path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          { path: 'product.media.main.video.posters.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
          { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'product.infoSections.description.metadata.updatedTimestamp',
          },
          {
            path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'product.physicalProperties.pricePerUnit.quantity' },
          { path: 'product.physicalProperties.shippingWeightRange.minValue' },
          { path: 'product.physicalProperties.shippingWeightRange.maxValue' },
          {
            path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          { path: 'product.variantsInfo.variants.revenueDetails.profitMargin' },
          {
            path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
          },
          { path: 'product.minVariantPriceInfo.revenueDetails.profitMargin' },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          { path: 'product.description.nodes.galleryData.options.item.ratio' },
          { path: 'product.description.nodes.imageData.image.duration' },
          { path: 'product.description.nodes.mapData.mapSettings.lat' },
          { path: 'product.description.nodes.mapData.mapSettings.lng' },
          { path: 'product.description.nodes.pollData.poll.image.duration' },
          {
            path: 'product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.description.nodes.pollData.design.poll.background.image.duration',
          },
          { path: 'product.description.nodes.appEmbedData.image.duration' },
          { path: 'product.description.nodes.videoData.video.duration' },
          { path: 'product.description.nodes.videoData.thumbnail.duration' },
          { path: 'product.description.nodes.audioData.audio.duration' },
          { path: 'product.description.nodes.audioData.coverImage.duration' },
          {
            path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          { path: 'product.description.nodes.shapeData.shape.duration' },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.description.nodes.tocData.fontSize' },
          { path: 'product.description.nodes.tocData.itemSpacing' },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          { path: 'product.infoSections.description.nodes.tocData.fontSize' },
          {
            path: 'product.infoSections.description.nodes.tocData.itemSpacing',
          },
          { path: 'product.variantsInfo.variants.physicalProperties.weight' },
          { path: 'product.minVariantPriceInfo.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkUpdateProductsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/update-by-filter',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __bulkUpdateProductsByFilter;
}

/** Deletes a product. */
export function deleteProduct(payload: object): RequestOptionsFactory<any> {
  function __deleteProduct({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'DELETE' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.DeleteProduct',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/{productId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteProduct;
}

/** Deletes multiple products. */
export function bulkDeleteProducts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteProducts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkDeleteProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteProducts;
}

/** Delete multiple products given the provided filter. */
export function bulkDeleteProductsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteProductsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkDeleteProductsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/delete-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteProductsByFilter;
}

/**
 * Retrieves a product.
 *
 * > **Note:**
 * > To retrieve a non-visible product (`visible: false`), your app must have the 'Product v3 read admin' (`SCOPE.STORES.PRODUCT_READ_ADMIN`) permission scope.
 */
export function getProduct(payload: object): RequestOptionsFactory<any> {
  function __getProduct({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.GetProduct',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/{productId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getProduct;
}

/**
 * Retrieves a product by slug.
 *
 * > **Note:**
 * > To retrieve a non-visible product (`visible: false`), your app must have the 'Product v3 read admin' (`SCOPE.STORES.PRODUCT_READ_ADMIN`) permission scope.
 */
export function getProductBySlug(payload: object): RequestOptionsFactory<any> {
  function __getProductBySlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.GetProductBySlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/slug/{slug}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'product.createdDate' },
              { path: 'product.updatedDate' },
              { path: 'product.description.metadata.createdTimestamp' },
              { path: 'product.description.metadata.updatedTimestamp' },
              { path: 'product.media.main.image.urlExpirationDate' },
              { path: 'product.media.main.video.urlExpirationDate' },
              {
                path: 'product.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'product.media.main.video.posters.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.image.urlExpirationDate' },
              { path: 'product.media.itemsInfo.items.video.urlExpirationDate' },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'product.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'product.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'product.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'product.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'product.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'product.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'product.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'product.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'product.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'product.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'product.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'product.description.nodes.imageData.image.duration' },
              { path: 'product.description.nodes.mapData.mapSettings.lat' },
              { path: 'product.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'product.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.description.nodes.pollData.design.poll.background.image.duration',
              },
              { path: 'product.description.nodes.appEmbedData.image.duration' },
              { path: 'product.description.nodes.videoData.video.duration' },
              {
                path: 'product.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'product.description.nodes.audioData.audio.duration' },
              {
                path: 'product.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'product.description.nodes.shapeData.shape.duration' },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'product.description.nodes.tocData.fontSize' },
              { path: 'product.description.nodes.tocData.itemSpacing' },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'product.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'product.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'product.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'product.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'product.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'product.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'product.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getProductBySlug;
}

/**
 * Retrieves a list of up to 100 products, given the provided filtering, search expression, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 * To learn about working with _Search_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 *
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 *
 * > **Notes:**
 * > + When using free-text search, you must specify which fields to search using the `search.fields` parameter.
 * > + To retrieve a non-visible product (`visible: false`), your app must have the 'Product v3 read admin' (`SCOPE.STORES.PRODUCT_READ_ADMIN`) permission scope.
 * > + This method does not return variant data. To retrieve variant data, call [Get Product](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/get-product) or [Get Product By Slug](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/get-product-by-slug) for each product individually, or use the [Read-Only Variants API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/read-only-variants-v3/introduction) for variant-centric queries.
 */
export function searchProducts(payload: object): RequestOptionsFactory<any> {
  function __searchProducts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.SearchProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/search',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'products.createdDate' },
              { path: 'products.updatedDate' },
              { path: 'products.description.metadata.createdTimestamp' },
              { path: 'products.description.metadata.updatedTimestamp' },
              { path: 'products.media.main.image.urlExpirationDate' },
              { path: 'products.media.main.video.urlExpirationDate' },
              {
                path: 'products.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'products.media.main.video.posters.urlExpirationDate' },
              {
                path: 'products.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'products.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'products.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'products.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'products.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'products.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'products.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'products.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'products.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'products.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'products.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'products.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'products.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'products.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'products.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'products.description.nodes.imageData.image.duration' },
              { path: 'products.description.nodes.mapData.mapSettings.lat' },
              { path: 'products.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'products.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'products.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'products.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'products.description.nodes.appEmbedData.image.duration',
              },
              { path: 'products.description.nodes.videoData.video.duration' },
              {
                path: 'products.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'products.description.nodes.audioData.audio.duration' },
              {
                path: 'products.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'products.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'products.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'products.description.nodes.shapeData.shape.duration' },
              {
                path: 'products.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'products.description.nodes.tocData.fontSize' },
              { path: 'products.description.nodes.tocData.itemSpacing' },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'products.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'products.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'products.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'products.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'products.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'products.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'products.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'products.minVariantPriceInfo.weight' },
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixStoresCatalogV3CatalogApiUrl({
            protoPath: '/v3/products/search',
            data: serializedData,
            host,
          }),
          data: serializedData,
        },
      ],
    };

    return metadata;
  }

  return __searchProducts;
}

/**
 * Retrieves a list of up to 100 products, given the provided filtering, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 *
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 *
 * > **Notes:**
 * > + To retrieve a non-visible product (`visible: false`), your app must have the 'Product v3 read admin' (`SCOPE.STORES.PRODUCT_READ_ADMIN`) permission scope.
 * > + This method does not return variant data. To retrieve variant data, call [Get Product](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/get-product) or [Get Product By Slug](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/get-product-by-slug) for each product individually, or use the [Read-Only Variants API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/read-only-variants-v3/introduction) for variant-centric queries.
 */
export function queryProducts(payload: object): RequestOptionsFactory<any> {
  function __queryProducts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.QueryProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'products.createdDate' },
              { path: 'products.updatedDate' },
              { path: 'products.description.metadata.createdTimestamp' },
              { path: 'products.description.metadata.updatedTimestamp' },
              { path: 'products.media.main.image.urlExpirationDate' },
              { path: 'products.media.main.video.urlExpirationDate' },
              {
                path: 'products.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.media.main.video.resolutions.poster.urlExpirationDate',
              },
              { path: 'products.media.main.video.posters.urlExpirationDate' },
              {
                path: 'products.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'products.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'products.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'products.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'products.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'products.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'products.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'products.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'products.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'products.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'products.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'products.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'products.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'products.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'products.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'products.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'products.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'products.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'products.description.nodes.galleryData.options.item.ratio',
              },
              { path: 'products.description.nodes.imageData.image.duration' },
              { path: 'products.description.nodes.mapData.mapSettings.lat' },
              { path: 'products.description.nodes.mapData.mapSettings.lng' },
              {
                path: 'products.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'products.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'products.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'products.description.nodes.appEmbedData.image.duration',
              },
              { path: 'products.description.nodes.videoData.video.duration' },
              {
                path: 'products.description.nodes.videoData.thumbnail.duration',
              },
              { path: 'products.description.nodes.audioData.audio.duration' },
              {
                path: 'products.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'products.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'products.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'products.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              { path: 'products.description.nodes.shapeData.shape.duration' },
              {
                path: 'products.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'products.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'products.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'products.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'products.description.nodes.tocData.fontSize' },
              { path: 'products.description.nodes.tocData.itemSpacing' },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'products.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'products.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'products.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'products.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'products.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'products.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'products.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'products.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'products.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'products.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'products.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'products.minVariantPriceInfo.weight' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixStoresCatalogV3CatalogApiUrl({
            protoPath: '/v3/products/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryProducts;
}

/** Counts the number of products that match the provided filtering. */
export function countProducts(payload: object): RequestOptionsFactory<any> {
  function __countProducts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.CountProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/products/count',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),

      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixStoresCatalogV3CatalogApiUrl({
            protoPath: '/v3/products/count',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __countProducts;
}

/**
 * Updates multiple product variants, given the provided filter and search expression.
 *
 * Only the following variant fields can be updated:
 * + `visible`
 * + `price`
 * + `revenueDetails.cost`
 * + `physicalProperties`
 */
export function bulkUpdateProductVariantsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProductVariantsByFilter({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'variant.physicalProperties.weight' },
          { path: 'variant.physicalProperties.pricePerUnit.settings.quantity' },
          { path: 'variant.revenueDetails.profitMargin' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'variant.media.image.urlExpirationDate' },
          { path: 'variant.media.video.urlExpirationDate' },
          { path: 'variant.media.video.resolutions.urlExpirationDate' },
          { path: 'variant.media.video.resolutions.poster.urlExpirationDate' },
          { path: 'variant.media.video.posters.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkUpdateProductVariantsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/update-variants-by-filter',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __bulkUpdateProductVariantsByFilter;
}

/**
 * Adjusts the prices and costs of multiple variants by a specified amount or percentage, given the provided filter and search expression.
 *
 * Use this method to apply bulk pricing changes, such as increasing all prices by 10%, without setting explicit values. Use [Bulk Update Product Variants By Filter](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/bulk-update-product-variants-by-filter) to set explicit values instead.
 *
 * The following variant fields can be increased or decreased:
 * + `compareAtPrice` - Original price before discounts
 * + `actualPrice` - Current selling price
 * + `cost` - Merchant's cost to acquire/produce the item
 * + `compareAtPriceDiscount` - Discount percentage applied to compare-at price
 */
export function bulkAdjustProductVariantsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAdjustProductVariantsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkAdjustProductVariantsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/adjust-variants-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkAdjustProductVariantsByFilter;
}

/** Adds [info sections](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/info-sections-v3/introduction) to multiple products, given the provided filter and search expression. */
export function bulkAddInfoSectionsToProductsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAddInfoSectionsToProductsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkAddInfoSectionsToProductsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/add-info-sections-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkAddInfoSectionsToProductsByFilter;
}

/** Adds [info sections](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/info-sections-v3/introduction) to multiple products. */
export function bulkAddInfoSectionsToProducts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAddInfoSectionsToProducts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkAddInfoSectionsToProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/add-info-sections',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.description.metadata.createdTimestamp' },
              { path: 'results.item.description.metadata.updatedTimestamp' },
              { path: 'results.item.media.main.image.urlExpirationDate' },
              { path: 'results.item.media.main.video.urlExpirationDate' },
              {
                path: 'results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.description.nodes.tocData.fontSize' },
              { path: 'results.item.description.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'results.item.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkAddInfoSectionsToProducts;
}

/** Removes [info sections](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/info-sections-v3/introduction) from multiple products, given the provided filter and search expression. */
export function bulkRemoveInfoSectionsFromProductsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkRemoveInfoSectionsFromProductsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkRemoveInfoSectionsFromProductsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/remove-info-sections-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkRemoveInfoSectionsFromProductsByFilter;
}

/** Removes [info sections](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/info-sections-v3/introduction) from multiple products. */
export function bulkRemoveInfoSectionsFromProducts(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkRemoveInfoSectionsFromProducts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkRemoveInfoSectionsFromProducts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/remove-info-sections',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.description.metadata.createdTimestamp' },
              { path: 'results.item.description.metadata.updatedTimestamp' },
              { path: 'results.item.media.main.image.urlExpirationDate' },
              { path: 'results.item.media.main.video.urlExpirationDate' },
              {
                path: 'results.item.media.main.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.main.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.image.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.media.itemsInfo.items.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.options.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
              },
              {
                path: 'results.item.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
              },
              {
                path: 'results.item.infoSections.description.metadata.createdTimestamp',
              },
              {
                path: 'results.item.infoSections.description.metadata.updatedTimestamp',
              },
              {
                path: 'results.item.variantsInfo.variants.media.image.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.resolutions.poster.urlExpirationDate',
              },
              {
                path: 'results.item.variantsInfo.variants.media.video.posters.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'results.item.physicalProperties.pricePerUnit.quantity' },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.minValue',
              },
              {
                path: 'results.item.physicalProperties.shippingWeightRange.maxValue',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
              },
              {
                path: 'results.item.variantsInfo.variants.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.subscriptionDetails.subscriptions.discount.percentOff',
              },
              {
                path: 'results.item.minVariantPriceInfo.pricePerUnitData.settings.quantity',
              },
              {
                path: 'results.item.minVariantPriceInfo.revenueDetails.profitMargin',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.description.nodes.cardData.backgroundImage.media.duration',
              },
              { path: 'results.item.description.nodes.tocData.fontSize' },
              { path: 'results.item.description.nodes.tocData.itemSpacing' },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.image.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.galleryData.options.item.ratio',
              },
              {
                path: 'results.item.infoSections.description.nodes.imageData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lat',
              },
              {
                path: 'results.item.infoSections.description.nodes.mapData.mapSettings.lng',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.poll.options.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.pollData.design.poll.background.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.appEmbedData.image.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.video.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.videoData.thumbnail.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.audio.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.audioData.coverImage.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdropImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.shapeData.shape.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerX',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.centerY',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.background.gradient.stops.position',
              },
              {
                path: 'results.item.infoSections.description.nodes.cardData.backgroundImage.media.duration',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.fontSize',
              },
              {
                path: 'results.item.infoSections.description.nodes.tocData.itemSpacing',
              },
              {
                path: 'results.item.variantsInfo.variants.physicalProperties.weight',
              },
              { path: 'results.item.minVariantPriceInfo.weight' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkRemoveInfoSectionsFromProducts;
}

/**
 * Adds multiple products to up to 5 categories, given the provided filter and search expression.
 *
 * Learn more about the [Categories API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction).
 */
export function bulkAddProductsToCategoriesByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAddProductsToCategoriesByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkAddProductsToCategoriesByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/add-to-categories-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkAddProductsToCategoriesByFilter;
}

/**
 * Removes multiple products, given the provided filter and search expression, from up to 5 categories.
 *
 * Learn more about the [Categories API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction).
 */
export function bulkRemoveProductsFromCategoriesByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkRemoveProductsFromCategoriesByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkRemoveProductsFromCategoriesByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/remove-from-categories-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkRemoveProductsFromCategoriesByFilter;
}

/**
 * Retrieves the ID of the "All Products" category, which is automatically assigned to all products.
 *
 * Learn more about the [Categories API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction).
 */
export function getAllProductsCategory(
  payload: object
): RequestOptionsFactory<any> {
  function __getAllProductsCategory({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'GET' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.GetAllProductsCategory',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/all-products-category',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getAllProductsCategory;
}

/**
 * Synchronously update tags on multiple products, by list of product IDs.
 *
 * > **Note:** If the same tag is passed to both the `assignTags` and `unassignTags` fields, it will be assigned.
 */
export function bulkUpdateProductTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProductTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn: 'wix.stores.catalog.v3.CatalogApi.BulkUpdateProductTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateProductTags;
}

/**
 * Asynchronously update tags on multiple products, by provided filter.
 * An empty filter will update all products.
 *
 * > **Note:** If the same tag is passed to both the `assignTags` and `unassignTags` fields, it will be assigned.
 */
export function bulkUpdateProductTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateProductTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.product',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.v3.CatalogApi.BulkUpdateProductTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogV3CatalogApiUrl({
        protoPath: '/v3/bulk/products/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateProductTagsByFilter;
}
